import React, { useState, useEffect, useRef, startTransition } from 'react';
import FormSection from '../FormSection';
import FormField from '../FormField';
import Input from '../Input';
import Textarea from '../Textarea';
import SearchableSelect from '../SearchableSelect';
import MultiSelectTags from '../MultiSelectTags';
import PhoneInput from '../PhoneInput';
import Modal from '../Modal';
import CustomSelect from '../CustomSelect';
import { ThematicAxis, CatalogSection as CatalogSectionType } from '../../types/profile.types';
import { 
  currentStatuses,
  solutionTypes,
  technologiesUsed,
  estimatedBeneficiaries,
  policyTypes,
  thematicAxes,
  subejesPorEje,
  catalogSections as catalogSectionsOptions
} from '../../data/profileData';
import CheckboxGroup from '../CheckboxGroup';
import { validateMultipleFiles, MAX_FILE_SIZE_DOCUMENTS } from '../../utils/fileValidation';
import { useFileError } from '../../contexts/FileErrorContext';
import { useUploadProgress } from '../../contexts/UploadProgressContext';
import { useTimeout } from '../../contexts/TimeoutContext';
import { uploadToSupabase } from '../../services/supabase';
import { handleRateLimitError } from '../../utils/rateLimitHandler';
import { handleUploadError } from '../../utils/uploadErrorHandler';
import { CheckCircleIcon, XCircleIcon, AlertTriangleIcon } from '../icons/AdminIcons';
import './ThematicAxesSection.css';
import './SpeakersSection.css';

// Helper function para obtener el estado de la iniciativa (igual que en AdminPanel)
const getInitiativeStatusHelper = (initiative: any): { completionStatus: 'complete' | 'incomplete', hasRequestApproval: boolean } => {
  if (!initiative) return { completionStatus: 'incomplete', hasRequestApproval: false };
  
  const hasName = initiative.name && initiative.name.toString().trim() !== '';
  const hasShortDescription = initiative.shortDescription && initiative.shortDescription.toString().trim() !== '';
  const hasDescription = initiative.description && initiative.description.toString().trim() !== '';
  const hasThematicAxes = initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0;
  let isComplete = hasName && hasShortDescription && hasDescription && hasThematicAxes;
  
  if (!initiative.noContact) {
    const hasContactFirstName = initiative.contactFirstName && initiative.contactFirstName.trim() !== '';
    const hasContactLastName = initiative.contactLastName && initiative.contactLastName.trim() !== '';
    const hasContactPosition = initiative.contactPosition && initiative.contactPosition.trim() !== '';
    const hasContactEmail = initiative.contactEmail && initiative.contactEmail.trim() !== '';
    isComplete = isComplete && hasContactFirstName && hasContactLastName && hasContactPosition && hasContactEmail;
  }
  
  return {
    completionStatus: isComplete ? 'complete' : 'incomplete',
    hasRequestApproval: initiative.requestApproval === true
  };
};

export type InitiativeType = 'solution' | 'challenge' | 'program' | 'policy' | '';

export interface Initiative {
  id: string;
  type?: InitiativeType;
  // Campos comunes
  name: string;
  shortDescription: string;
  description: string;
  currentStatus: 'en_diseno' | 'piloto' | 'implementado' | 'escalando' | '';
  thematicAxes?: ThematicAxis[];
  organizationArea?: string;
  territory: string;
  population: string;
  results: string;
  approach: string;
  alliances: string;
  supportDocuments?: (File | string)[];
  // Campos específicos de Soluciones
  solutionType?: 'software' | 'hardware' | 'infraestructura' | 'servicio' | 'metodologico' | 'hibrido' | 'otro' | '' | undefined;
  technologiesUsed?: string[];
  // Campos específicos de Programas
  estimatedBeneficiaries?: 'hasta_500' | '500_5000' | '5000_50000' | 'mas_50000' | 'no_aplica' | '';
  // Campos específicos de Políticas
  policyType?: 'programa' | 'piloto' | 'politica' | 'estrategia' | 'servicio' | 'infraestructura' | 'otro' | '';
  // Campo de aprobación
  requestApproval?: boolean;
  approval_status?: 'pending' | 'approved' | 'rejected';
  // Campos de contacto
  noContact?: boolean;
  doNotPublishContact?: boolean;
  contactFirstName?: string;
  contactLastName?: string;
  contactPosition?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactCodigoPais?: string;
  contactNumeroTelefono?: string;
  // Rondas de Negocios
  interestedInBusinessRounds?: boolean;
  interestedInBusinessRoundsOutsideEvent?: boolean;
}

interface InitiativesSectionProps {
  initiatives: Initiative[];
  catalogSections?: CatalogSectionType[];
  onInitiativesChange: (initiatives: Initiative[]) => void;
  onCatalogSectionsChange?: (sections: CatalogSectionType[]) => void;
  readOnly?: boolean;
  hideTitle?: boolean;
  initialExpandedInitiativeId?: string;
  showApprovalBadge?: boolean;
  onApprovalStatusChange?: (initiativeId: string, status: 'pending' | 'approved' | 'rejected') => void;
  userId?: number;
}

const InitiativesSection: React.FC<InitiativesSectionProps> = ({
  initiatives: initiativesProp,
  catalogSections = [],
  onInitiativesChange,
  onCatalogSectionsChange,
  readOnly = false,
  hideTitle = false,
  initialExpandedInitiativeId,
  showApprovalBadge = false,
  onApprovalStatusChange,
  userId
}) => {

  // Ordenar iniciativas por orden de creación (usando el ID que incluye timestamp)
  // Las iniciativas se ordenan cronológicamente por su timestamp, sin importar el tipo
  const sortedInitiatives = [...initiativesProp].sort((a, b) => {
    // Extraer el timestamp del ID si es posible
    const getTimestamp = (id: string): number => {
      // Si el ID tiene formato timestamp-random (ej: "1234567890-abc123"), extraer el timestamp
      const match = id.match(/^(\d+)-/);
      if (match) {
        return parseInt(match[1], 10);
      }
      // Si el ID es solo un número (timestamp puro), usarlo directamente
      if (/^\d+$/.test(id)) {
        return parseInt(id, 10);
      }
      // Si no tiene formato timestamp reconocible, usar un número muy grande para que aparezcan al final
      // Esto asegura que las iniciativas nuevas (con timestamp) aparezcan antes que las antiguas sin timestamp
      return Number.MAX_SAFE_INTEGER;
    };

    const timestampA = getTimestamp(a.id);
    const timestampB = getTimestamp(b.id);

    // Ordenar por timestamp (más antiguo primero, más reciente al final)
    return timestampA - timestampB;
  });

  const [newInitiativeIds, setNewInitiativeIds] = useState<Set<string>>(new Set());

  const addInitiative = () => {
    // Crear una iniciativa sin tipo inicial
    // Generar ID único usando timestamp y random string
    const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setNewInitiativeIds(prev => new Set(prev).add(newId));
    const newInitiative: Initiative = {
      id: newId,
      type: '', // Tipo vacío inicialmente, se establecerá cuando el usuario seleccione
      name: '',
      shortDescription: '',
      description: '',
      currentStatus: '',
      thematicAxes: [],
      organizationArea: '',
      territory: '',
      population: '',
      results: '',
      approach: '',
      alliances: '',
      solutionType: '',
      technologiesUsed: [],
      requestApproval: false,
      noContact: false,
      doNotPublishContact: false,
      contactFirstName: '',
      contactLastName: '',
      contactPosition: '',
      contactEmail: '',
      contactPhone: '',
      contactCodigoPais: '',
      contactNumeroTelefono: '',
      interestedInBusinessRounds: false,
      interestedInBusinessRoundsOutsideEvent: false
    };
    onInitiativesChange([...initiativesProp, newInitiative]);
  };

  // Función para actualizar múltiples campos a la vez
  const updateInitiativeMultiple = (id: string, updates: Partial<Initiative>) => {
    const updatedInitiatives = initiativesProp.map(init => 
      init.id === id ? { ...init, ...updates } : init
    );
    onInitiativesChange(updatedInitiatives);
  };

  const updateInitiative = (id: string, field: keyof Initiative, value: any) => {
    const updatedInitiatives = initiativesProp.map(init => 
      init.id === id ? { ...init, [field]: value } : init
    );
    onInitiativesChange(updatedInitiatives);
  };

  const removeInitiative = (id: string) => {
    const updatedInitiatives = initiativesProp.filter(init => init.id !== id);
    onInitiativesChange(updatedInitiatives);
  };

  // Estado para el tooltip de secciones
  const [visibleTooltipSecciones, setVisibleTooltipSecciones] = useState<boolean>(false);
  const [tooltipPositionSecciones, setTooltipPositionSecciones] = useState<{ left: number; top: number } | null>(null);
  const tooltipRefSecciones = useRef<HTMLDivElement | null>(null);
  const [isMobileOrTabletSecciones, setIsMobileOrTabletSecciones] = useState(false);

  // Detectar si es mobile o tablet para el tooltip de secciones
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTabletSecciones(window.innerWidth <= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Efecto para manejar el tooltip de secciones en el DOM
  useEffect(() => {
    if (tooltipRefSecciones.current && tooltipRefSecciones.current.parentNode) {
      tooltipRefSecciones.current.parentNode.removeChild(tooltipRefSecciones.current);
      tooltipRefSecciones.current = null;
    }

    if (visibleTooltipSecciones && tooltipPositionSecciones !== null) {
      const isDarkMode = document.documentElement.classList.contains('dark-theme');

      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${tooltipPositionSecciones.left}px !important;
        top: ${tooltipPositionSecciones.top}px !important;
        z-index: 999999 !important;
        background-color: ${isDarkMode ? '#0f172a' : '#ffffff'} !important;
        border: 2px solid #CE2B15 !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      `;

      const textDiv = document.createElement('div');
      textDiv.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6;`;
      textDiv.textContent = 'Este catálogo es el directorio del ecosistema de ciudades inteligentes de Latam. Reúne a empresas, startup, gobiernos, universidades y organizaciones con impacto, que están liderando la transformación en ciudades, territorios y comunidades. Ser parte del catálogo otorga visibilidad, vinculación estratégica, acceso a rondas de negocios, oportunidades de colaboración y presencia dentro de la red de innovación de Smart City Expo Santiago del Estero.';

      tooltipDiv.appendChild(textDiv);

      if (document.body) {
        document.body.appendChild(tooltipDiv);
        tooltipRefSecciones.current = tooltipDiv;
      }

      return () => {
        if (tooltipRefSecciones.current && tooltipRefSecciones.current.parentNode) {
          tooltipRefSecciones.current.parentNode.removeChild(tooltipRefSecciones.current);
        }
        tooltipRefSecciones.current = null;
      };
    }
  }, [visibleTooltipSecciones, tooltipPositionSecciones]);

  // Cerrar tooltip de secciones al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (!isMobileOrTabletSecciones || !visibleTooltipSecciones) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRefSecciones.current && !tooltipRefSecciones.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('.modalidad-info-icon')) {
          setVisibleTooltipSecciones(false);
          setTooltipPositionSecciones(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOrTabletSecciones, visibleTooltipSecciones]);

  const handleInfoIconClickSecciones = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (visibleTooltipSecciones) {
      setVisibleTooltipSecciones(false);
      setTooltipPositionSecciones(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipWidth = 400;
    const spacing = 10;

    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
    let top = rect.bottom + spacing;

    if (left + tooltipWidth > window.innerWidth) {
      left = window.innerWidth - tooltipWidth - 10;
    }
    if (left < 10) {
      left = 10;
    }

    const tooltipHeight = 200;
    if (top + tooltipHeight > window.innerHeight) {
      top = rect.top - tooltipHeight - spacing;
    }
    if (top < 10) {
      top = 10;
    }

    setVisibleTooltipSecciones(true);
    setTooltipPositionSecciones({ left, top });
  };

  const handleInfoIconHoverSecciones = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!isMobileOrTabletSecciones) {
      const rect = e.currentTarget.getBoundingClientRect();
      const tooltipWidth = 400;
      const spacing = 10;

      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      let top = rect.bottom + spacing;

      if (left + tooltipWidth > window.innerWidth) {
        left = window.innerWidth - tooltipWidth - 10;
      }
      if (left < 10) {
        left = 10;
      }

      const tooltipHeight = 200;
      if (top + tooltipHeight > window.innerHeight) {
        top = rect.top - tooltipHeight - spacing;
      }
      if (top < 10) {
        top = 10;
      }

      setVisibleTooltipSecciones(true);
      setTooltipPositionSecciones({ left, top });
    }
  };

  const handleInfoIconLeaveSecciones = () => {
    if (!isMobileOrTabletSecciones) {
      setVisibleTooltipSecciones(false);
      setTooltipPositionSecciones(null);
    }
  };

  return (
    <FormSection 
      title={hideTitle ? undefined : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Catálogo / Rondas de negocios</span>
          <span
            className="modalidad-info-icon"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              border: '1px solid #d1d5db',
              flexShrink: 0,
              transition: 'all 0.2s ease',
              display: 'flex'
            }}
            onClick={handleInfoIconClickSecciones}
            onMouseEnter={handleInfoIconHoverSecciones}
            onMouseLeave={handleInfoIconLeaveSecciones}
          >
            ?
          </span>
        </div>
      )}
      description={hideTitle ? undefined : (
        <>
          <p className="form-section-description" style={{ fontSize: '14px', color: '#64748b', margin: 0, marginBottom: '24px' }}>
            Conoce más acerca del{' '}
            <a 
              href="https://smartcityexposantiagodelestero.com/catalogo-organizaciones-que-transforman-2026/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#CE2B15', 
                textDecoration: 'none',
                fontWeight: 600
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Catálogo 2026
            </a>
            {' '}y de las{' '}
            <a 
              href="https://smartcityexposantiagodelestero.com/rondas-de-negocios/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#CE2B15', 
                textDecoration: 'none',
                fontWeight: 600
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Rondas de negocios
            </a>
          </p>
          <div className="initiative-section-divider" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '2px solid #e5e7eb' }}>
            {/* Campo de Secciones del Catálogo */}
            {(onCatalogSectionsChange || readOnly) && (
              <FormField label="Secciones" required>
                <CheckboxGroup
                  options={catalogSectionsOptions.map(section => ({ value: String(section.value), label: section.label }))}
                  selectedValues={Array.isArray(catalogSections) ? catalogSections.map(s => String(s)) : []}
                  onChange={(values) => onCatalogSectionsChange ? onCatalogSectionsChange(values as CatalogSectionType[]) : undefined}
                  error={false}
                  readOnly={readOnly}
                />
              </FormField>
            )}
          </div>
          <div style={{ marginTop: '60px' }}>
            <h3 className="initiatives-subsection-title" style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#1e293b',
              marginBottom: '8px',
              marginTop: 0
            }}>
              Publicar Iniciativas e Intereses
            </h3>
          </div>
        </>
      )}
    >
      {sortedInitiatives.map((initiative) => {
        return (
          <InitiativeCard
            key={initiative.id}
            initiative={initiative}
            onUpdate={(field, value) => updateInitiative(initiative.id, field, value)}
            onUpdateMultiple={(updates) => updateInitiativeMultiple(initiative.id, updates)}
            onRemove={() => removeInitiative(initiative.id)}
            readOnly={readOnly}
            initialExpanded={newInitiativeIds.has(initiative.id) || (initialExpandedInitiativeId !== undefined && initiative.id === initialExpandedInitiativeId)}
            showApprovalBadge={showApprovalBadge}
            onApprovalStatusChange={onApprovalStatusChange}
            userId={userId}
          />
        );
      })}

      {!readOnly && (
        <div style={{ position: 'relative' }}>
          <button 
            type="button" 
            className="add-button" 
            onClick={addInitiative}
            disabled={!catalogSections || catalogSections.length === 0}
            style={{
              marginTop: '0px',
              opacity: (!catalogSections || catalogSections.length === 0) ? 0.6 : 1,
              cursor: (!catalogSections || catalogSections.length === 0) ? 'not-allowed' : 'pointer'
            }}
            title={(!catalogSections || catalogSections.length === 0) ? 'Debes seleccionar al menos una sección del catálogo antes de agregar iniciativas' : ''}
          >
            + Agregar Iniciativa
          </button>
          {(!catalogSections || catalogSections.length === 0) && (
            <p style={{
              marginTop: '8px',
              fontSize: '13px',
              color: '#ef4444',
              fontFamily: 'Montserrat, sans-serif',
              marginBottom: 0
            }}>
              Debes seleccionar al menos una sección del catálogo antes de agregar iniciativas
            </p>
          )}
        </div>
      )}
    </FormSection>
  );
};

interface InitiativeCardProps {
  initiative: Initiative;
  onUpdate: (field: keyof Initiative, value: any) => void;
  onUpdateMultiple?: (updates: Partial<Initiative>) => void;
  onRemove: () => void;
  readOnly?: boolean;
  initialExpanded?: boolean;
  showApprovalBadge?: boolean;
  onApprovalStatusChange?: (initiativeId: string, status: 'pending' | 'approved' | 'rejected') => void;
  userId?: number;
}

// Función para capitalizar primera letra
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Países con dialCode y flag (igual que PhoneInput)
const countries = [
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'BR', name: 'Brasil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
  { code: 'MX', name: 'México', dialCode: '+52', flag: '🇲🇽' },
  { code: 'PE', name: 'Perú', dialCode: '+51', flag: '🇵🇪' },
  { code: 'UY', name: 'Uruguay', dialCode: '+598', flag: '🇺🇾' },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
  { code: 'BO', name: 'Bolivia', dialCode: '+591', flag: '🇧🇴' },
  { code: 'EC', name: 'Ecuador', dialCode: '+593', flag: '🇪🇨' },
  { code: 'PY', name: 'Paraguay', dialCode: '+595', flag: '🇵🇾' },
  { code: 'CR', name: 'Costa Rica', dialCode: '+506', flag: '🇨🇷' },
  { code: 'PA', name: 'Panamá', dialCode: '+507', flag: '🇵🇦' },
  { code: 'GT', name: 'Guatemala', dialCode: '+502', flag: '🇬🇹' },
  { code: 'HN', name: 'Honduras', dialCode: '+504', flag: '🇭🇳' },
  { code: 'SV', name: 'El Salvador', dialCode: '+503', flag: '🇸🇻' },
  { code: 'NI', name: 'Nicaragua', dialCode: '+505', flag: '🇳🇮' },
  { code: 'CU', name: 'Cuba', dialCode: '+53', flag: '🇨🇺' },
  { code: 'DO', name: 'República Dominicana', dialCode: '+1', flag: '🇩🇴' },
  { code: 'HT', name: 'Haití', dialCode: '+509', flag: '🇭🇹' },
  { code: 'JM', name: 'Jamaica', dialCode: '+1', flag: '🇯🇲' },
  { code: 'TT', name: 'Trinidad y Tobago', dialCode: '+1', flag: '🇹🇹' },
  { code: 'US', name: 'Estados Unidos', dialCode: '+1', flag: '🇺🇸' },
  { code: 'CA', name: 'Canadá', dialCode: '+1', flag: '🇨🇦' },
  { code: 'ES', name: 'España', dialCode: '+34', flag: '🇪🇸' },
  { code: 'IT', name: 'Italia', dialCode: '+39', flag: '🇮🇹' },
  { code: 'FR', name: 'Francia', dialCode: '+33', flag: '🇫🇷' },
  { code: 'DE', name: 'Alemania', dialCode: '+49', flag: '🇩🇪' },
  { code: 'GB', name: 'Reino Unido', dialCode: '+44', flag: '🇬🇧' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'NL', name: 'Países Bajos', dialCode: '+31', flag: '🇳🇱' },
  { code: 'BE', name: 'Bélgica', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CH', name: 'Suiza', dialCode: '+41', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
  { code: 'SE', name: 'Suecia', dialCode: '+46', flag: '🇸🇪' },
  { code: 'NO', name: 'Noruega', dialCode: '+47', flag: '🇳🇴' },
  { code: 'DK', name: 'Dinamarca', dialCode: '+45', flag: '🇩🇰' },
  { code: 'FI', name: 'Finlandia', dialCode: '+358', flag: '🇫🇮' },
  { code: 'PL', name: 'Polonia', dialCode: '+48', flag: '🇵🇱' },
  { code: 'CZ', name: 'República Checa', dialCode: '+420', flag: '🇨🇿' },
  { code: 'GR', name: 'Grecia', dialCode: '+30', flag: '🇬🇷' },
  { code: 'IE', name: 'Irlanda', dialCode: '+353', flag: '🇮🇪' },
  { code: 'RU', name: 'Rusia', dialCode: '+7', flag: '🇷🇺' },
  { code: 'TR', name: 'Turquía', dialCode: '+90', flag: '🇹🇷' },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱' },
  { code: 'AE', name: 'Emiratos Árabes Unidos', dialCode: '+971', flag: '🇦🇪' },
  { code: 'SA', name: 'Arabia Saudita', dialCode: '+966', flag: '🇸🇦' },
  { code: 'ZA', name: 'Sudáfrica', dialCode: '+27', flag: '🇿🇦' },
  { code: 'EG', name: 'Egipto', dialCode: '+20', flag: '🇪🇬' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'JP', name: 'Japón', dialCode: '+81', flag: '🇯🇵' },
  { code: 'KR', name: 'Corea del Sur', dialCode: '+82', flag: '🇰🇷' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'NZ', name: 'Nueva Zelanda', dialCode: '+64', flag: '🇳🇿' },
];

const InitiativeCard: React.FC<InitiativeCardProps> = ({ initiative, onUpdate, onUpdateMultiple, onRemove, readOnly = false, initialExpanded = false, showApprovalBadge = false, onApprovalStatusChange, userId }) => {
  const [visibleTooltip, setVisibleTooltip] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [filePreviews, setFilePreviews] = useState<Array<{ name: string; data: string }>>([]);
  const [emailError, setEmailError] = useState<string>('');
  
  const [isExpanded, setIsExpanded] = useState<boolean>(initialExpanded);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { showFileSizeError } = useFileError();
  const { startUpload, updateProgress, finishUpload, setCurrentFileName } = useUploadProgress();
  const { showTimeout } = useTimeout();

  // Detectar si es mobile o tablet
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Efecto para manejar previews de archivos
  useEffect(() => {
    const blobUrls: string[] = [];

    if (initiative.supportDocuments && Array.isArray(initiative.supportDocuments)) {
      const previews: Array<{ name: string; data: string }> = [];

      initiative.supportDocuments.forEach((doc) => {
        if (typeof doc === 'string' && doc.startsWith('FILENAME:')) {
          // Archivo guardado en la BD (formato: FILENAME:nombre:URL:url)
          const match = doc.match(/FILENAME:(.+?):URL:(.+)/);
          if (match) {
            previews.push({ name: match[1], data: match[2] });
          }
        } else if (doc instanceof File) {
          // Archivo nuevo subido (objeto File)
          const blobUrl = URL.createObjectURL(doc);
          blobUrls.push(blobUrl);
          previews.push({ name: doc.name, data: blobUrl });
        }
      });

      setFilePreviews(previews);
    } else {
      setFilePreviews([]);
    }

    // Cleanup: revocar URLs de objetos creados para evitar memory leaks
    return () => {
      blobUrls.forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [initiative.supportDocuments]);

  // Efecto para manejar tooltips de ejes temáticos
  useEffect(() => {
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }

    if (visibleTooltip !== null && tooltipPosition !== null) {
      const ejeSeleccionado = thematicAxes[visibleTooltip];
      const subejes = subejesPorEje[ejeSeleccionado.label]?.subejes || [];

      if (subejes.length === 0) return;

      const isDarkMode = document.documentElement.classList.contains('dark-theme');

      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'tooltip-content';
      tooltipDiv.style.cssText = `
        position: fixed !important;
        left: ${tooltipPosition.left}px !important;
        top: ${tooltipPosition.top}px !important;
        z-index: 999999 !important;
        background-color: ${isDarkMode ? '#0f172a' : '#ffffff'} !important;
        border: 2px solid #CE2B15 !important;
        border-radius: 8px !important;
        padding: 12px 16px !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        min-width: 280px !important;
        max-width: 400px !important;
        font-family: Montserrat, sans-serif !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
      `;

      const titleDiv = document.createElement('div');
      titleDiv.style.cssText = `font-weight: 700; color: ${isDarkMode ? '#ffffff' : '#1e293b'}; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${isDarkMode ? '#334155' : '#e5e7eb'};`;
      titleDiv.textContent = 'Incluye:';

      const ul = document.createElement('ul');
      ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';

      subejes.forEach((subeje) => {
        const li = document.createElement('li');
        li.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;`;

        const bullet = document.createElement('span');
        bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
        bullet.textContent = '•';

        li.appendChild(bullet);
        li.appendChild(document.createTextNode(subeje));
        ul.appendChild(li);
      });

      tooltipDiv.appendChild(titleDiv);
      tooltipDiv.appendChild(ul);

      if (document.body) {
        document.body.appendChild(tooltipDiv);
        tooltipRef.current = tooltipDiv;
      }

      return () => {
        if (tooltipRef.current && tooltipRef.current.parentNode) {
          tooltipRef.current.parentNode.removeChild(tooltipRef.current);
        }
        tooltipRef.current = null;
      };
    }
  }, [visibleTooltip, tooltipPosition]);

  const getTypeLabel = (type: InitiativeType | undefined) => {
    switch (type) {
      case 'solution':
        return 'Solución';
      case 'challenge':
        return 'Desafío';
      case 'program':
        return 'Programa de Impacto';
      case 'policy':
        return 'Política Pública';
      default:
        return '';
    }
  };

  // Función helper para obtener el texto del tipo de iniciativa en minúsculas (para placeholders)
  const getInitiativeTypeText = (type: InitiativeType | undefined) => {
    switch (type) {
      case 'solution':
        return 'solución';
      case 'challenge':
        return 'desafío';
      case 'program':
        return 'programa de impacto';
      case 'policy':
        return 'política pública';
      default:
        return 'iniciativa';
    }
  };

  // Función helper para obtener la preposición correcta según el género del tipo de iniciativa
  const getInitiativePreposition = (type: InitiativeType | undefined) => {
    switch (type) {
      case 'solution':
        return 'de la'; // solución (femenino)
      case 'challenge':
        return 'del'; // desafío (masculino)
      case 'program':
        return 'del'; // programa (masculino)
      case 'policy':
        return 'de la'; // política (femenino)
      default:
        return 'de la'; // iniciativa (femenino)
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);

      // Validar tamaño de archivos antes de agregarlos
      const validation = validateMultipleFiles(newFiles, MAX_FILE_SIZE_DOCUMENTS, 'document');
      if (!validation.valid) {
        showFileSizeError(validation.error || 'El archivo excede el tamaño máximo permitido.');
        e.target.value = '';
        return;
      }

      try {
        // Iniciar progreso de subida
        startUpload(newFiles[0].name, newFiles.length);

        // Subir archivos de forma secuencial (uno después del otro)
        const uploadedUrls: string[] = [];
        for (let index = 0; index < newFiles.length; index++) {
          const file = newFiles[index];
          // Actualizar el nombre del archivo actual
          setCurrentFileName(file.name);
          const docUrl = await uploadToSupabase(file, {
            folder: 'perfil-institucional/initiatives/documents',
            onProgress: (progress) => {
              // Calcular progreso total considerando todos los archivos
              // Cada archivo completo representa (100 / totalArchivos)%
              const baseProgress = (index / newFiles.length) * 100;
              const fileProgress = (progress / newFiles.length);
              const totalProgress = baseProgress + fileProgress;
              updateProgress(totalProgress, index);
            }
          });
          // Log para debugging
          
          // Guardar con formato: "FILENAME:nombre_archivo:URL:url"
          uploadedUrls.push(`FILENAME:${file.name}:URL:${docUrl}`);
        }

        // Finalizar progreso
        finishUpload();

        const currentDocs = initiative.supportDocuments || [];
        // Agregar las URLs al estado
        const updatedDocs = [...currentDocs, ...uploadedUrls];
        
        // Log para debugging
        
        onUpdate('supportDocuments', updatedDocs.length > 0 ? updatedDocs : undefined);
      } catch (error: any) {
        // Verificar si es un error de rate limiting (429)
        if (handleRateLimitError(error)) {
          finishUpload();
          return;
        }
        
        // Verificar si es un error de formato o tamaño del backend
        if (handleUploadError(error, showFileSizeError)) {
          finishUpload();
          return;
        }
        
        // Verificar si es un error de timeout
        if (error?.isTimeout) {
          showTimeout(error.operationName || 'la subida de los documentos');
          finishUpload();
          return;
        }
        
        // Finalizar progreso incluso si hay error
        finishUpload();
        // Error silencioso para otros tipos de error
      }

      // Limpiar el input para permitir subir el mismo archivo nuevamente
      e.target.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const currentDocs = initiative.supportDocuments || [];
    const updatedDocs = currentDocs.filter((_, i) => i !== index);
    onUpdate('supportDocuments', updatedDocs.length > 0 ? updatedDocs : undefined);
  };

  // Funciones para manejar cambios en campos de contacto
  const handleContactCodigoPaisChange = (codigo: string) => {
    const selectedCountry = countries.find(c => c.code === codigo);
    const dialCode = selectedCountry?.dialCode || '';
    const currentNumero = initiative.contactNumeroTelefono || '';
    const phone = currentNumero.trim() ? `${dialCode} ${currentNumero.trim()}` : dialCode;

    // Actualizar ambos campos en una sola operación si es posible
    if (onUpdateMultiple) {
      onUpdateMultiple({
        contactCodigoPais: codigo,
        contactPhone: phone
      });
    } else {
      // Fallback: actualizar campo por campo
      onUpdate('contactCodigoPais', codigo);
      onUpdate('contactPhone', phone);
    }
  };

  const handleContactNumeroTelefonoChange = (numero: string) => {
    const currentCodigo = initiative.contactCodigoPais || 'AR';
    const selectedCountry = countries.find(c => c.code === currentCodigo) || countries[0];
    const dialCode = selectedCountry?.dialCode || '';
    const phone = numero.trim() ? `${dialCode} ${numero.trim()}` : dialCode;

    // Actualizar ambos campos en una sola operación si es posible
    if (onUpdateMultiple) {
      onUpdateMultiple({
        contactNumeroTelefono: numero,
        contactPhone: phone
      });
    } else {
      // Fallback: actualizar campo por campo
      onUpdate('contactNumeroTelefono', numero);
      onUpdate('contactPhone', phone);
    }
  };

  const handleContactFieldBlur = (field: 'contactFirstName' | 'contactLastName' | 'contactPosition', value: string) => {
    const capitalized = capitalizeFirstLetter(value);
    if (capitalized !== value) {
      onUpdate(field, capitalized);
    }
  };

  // Función para manejar blur de campos principales (no de contacto)
  const handleFieldBlur = (field: 'name' | 'organizationArea' | 'territory', value: string) => {
    const capitalized = capitalizeFirstLetter(value);
    if (capitalized !== value) {
      onUpdate(field, capitalized);
    }
  };

  // Función para validar formato de email
  const validateEmail = (email: string): boolean => {
    if (!email || email.trim() === '') return true; // Campo vacío es válido (se valida con required)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleContactEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onUpdate('contactEmail', value);
  };

  // Validar email continuamente para que el error persista mientras haya contenido inválido
  useEffect(() => {
    const email = initiative.contactEmail || '';
    if (email.trim() === '') {
      setEmailError('');
    } else if (!validateEmail(email)) {
      setEmailError('Por favor, ingresa un correo electrónico válido');
    } else {
      setEmailError('');
    }
  }, [initiative.contactEmail]);

  const handleContactEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    // Validar al perder el foco
    if (value && !validateEmail(value)) {
      setEmailError('Por favor, ingresa un correo electrónico válido');
    } else {
      setEmailError('');
    }
  };

  const isContactDisabled = initiative.noContact || false;

  return (
    <div className="solution-card" data-initiative-type={initiative.type} style={{ position: 'relative', overflow: 'visible' }}>
      {/* Badges de estado en el borde superior derecho (solo en readonly del popup) */}
      {readOnly && showApprovalBadge && (() => {
        const statusInfo = getInitiativeStatusHelper(initiative);
        // Solo mostrar selector de aprobación si está completo o tiene solicitud de aprobación
        const shouldShowApprovalSelector = statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval;
        
        return (
          <div className="admin-participation-card-status-badges" style={{ marginRight: '10px' }}>
            {/* Badge de estado de completitud - siempre se muestra si corresponde */}
            <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
              {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
              {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
            </span>
            {/* Badge de solicitado - siempre se muestra si corresponde */}
            {statusInfo.hasRequestApproval && (
              <span className="participation-status-badge status-requested">
                <AlertTriangleIcon width={12} height={12} /> Solicitado
              </span>
            )}
            {/* Selector de estado de aprobación - solo se muestra si está completo o tiene solicitud */}
            {shouldShowApprovalSelector && onApprovalStatusChange && (
              <div style={{ minWidth: '120px', maxWidth: '140px', flexShrink: 0 }}>
                <CustomSelect
                  className="admin-filter-select admin-filter-select-small"
                  value={initiative.approval_status || 'pending'}
                  onChange={(newStatus) => {
                    if (onApprovalStatusChange) {
                      onApprovalStatusChange(initiative.id, newStatus as 'pending' | 'approved' | 'rejected');
                    }
                  }}
                  options={[
                    { value: 'pending', label: 'Pendiente', color: '#3b82f6' },
                    { value: 'approved', label: 'Aprobado', color: '#10b981' },
                    { value: 'rejected', label: 'Rechazado', color: '#ef4444' }
                  ]}
                />
              </div>
            )}
          </div>
        );
      })()}
      <div className="solution-header">
        <h3>{initiative.name || 'Nueva Iniciativa'}</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            className="minimize-button"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Minimizar' : 'Expandir'}
            style={{
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '14px',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 500
            }}
          >
            {isExpanded ? '−' : '+'}
          </button>
          {!readOnly && (
            <button 
              type="button" 
              className="remove-button"
              onClick={() => setShowDeleteModal(true)}
            >
              Eliminar
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
      <div className="solution-card-content">
      {/* Selector de tipo de iniciativa */}
      <FormField label="Tipo de Iniciativa" required>
        <SearchableSelect
          value={getTypeLabel(initiative.type)}
          onChange={(label) => {

            let typeValue: InitiativeType = '';
            if (label === 'Solución') {
              typeValue = 'solution';
            } else if (label === 'Desafío') {
              typeValue = 'challenge';
            } else if (label === 'Programa de Impacto') {
              typeValue = 'program';
            } else if (label === 'Política Pública') {
              typeValue = 'policy';
            } else {
            }

            if (typeValue) {
              onUpdate('type', typeValue);
            } else {
            }
          }}
          options={['Solución', 'Desafío', 'Programa de Impacto', 'Política Pública']}
          placeholder="Selecciona el tipo de iniciativa"
          allowCustomInput={false}
          disabled={readOnly}
        />
      </FormField>

      {/* Mostrar todos los demás campos solo si hay un tipo seleccionado */}
      {initiative.type && (
        <>
          {/* Campos comunes */}
      <FormField label="Nombre" required>
        <Input
          value={initiative.name}
          onChange={(e) => onUpdate('name', e.target.value)}
          onBlur={(e) => handleFieldBlur('name', e.target.value)}
          placeholder={`Nombre ${getInitiativePreposition(initiative.type)} ${getInitiativeTypeText(initiative.type)}`}
          disabled={readOnly}
        />
      </FormField>

      <FormField label="Descripción amplia" required>
        <Textarea
          value={initiative.description || ''}
          onChange={(e) => onUpdate('description', e.target.value)}
          placeholder={`Descripción amplia ${getInitiativePreposition(initiative.type)} ${getInitiativeTypeText(initiative.type)}...`}
          rows={4}
          maxLength={1000}
          showCharCount={true}
          disabled={readOnly}
        />
      </FormField>

      <FormField label="Descripción breve" required>
        <Textarea
          value={initiative.shortDescription || ''}
          onChange={(e) => onUpdate('shortDescription', e.target.value)}
          placeholder={`Descripción breve ${getInitiativePreposition(initiative.type)} ${getInitiativeTypeText(initiative.type)}...`}
          rows={3}
          maxLength={500}
          showCharCount={true}
          disabled={readOnly}
        />
      </FormField>

      {/* Ejes temáticos - movido aquí, después de Objetivo principal */}
      <FormField
        label="Ejes temáticos vinculados"
        required
      >
        <div className="checkbox-group thematic-axes-two-columns">
          {/* Selector "Seleccionar todos" */}
          <label 
            className="checkbox-item select-all-checkbox"
            style={{
              fontWeight: 600,
              marginBottom: '8px',
              width: '100%'
            }}
          >
            <input
              type="checkbox"
              checked={
                (initiative.thematicAxes || []).length > 0 &&
                (initiative.thematicAxes || []).length === thematicAxes.length
              }
              onChange={(e) => {
                if (readOnly) return;
                const currentAxes = initiative.thematicAxes || [];
                if (e.target.checked) {
                  // Seleccionar todos
                  const allAxes = thematicAxes.map(axis => axis.value);
                  onUpdate('thematicAxes', allAxes);
                } else {
                  // Deseleccionar todos
                  onUpdate('thematicAxes', []);
                }
              }}
              disabled={readOnly}
            />
            <span className="checkbox-label">Seleccionar todos</span>
          </label>
          {thematicAxes.map((option, index) => {
            const isChecked = (initiative.thematicAxes || []).includes(option.value);
            const subejes = subejesPorEje[option.label]?.subejes || [];
            const hasSubejes = subejes.length > 0;

            return (
              <label 
                key={option.value} 
                className={`checkbox-item ${hasSubejes ? 'has-tooltip' : ''}`}
                onMouseEnter={(e) => {
                  if (hasSubejes && !isMobileOrTablet) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const tooltipWidth = 300;
                    const spacing = 10;

                    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                    let top = rect.bottom + spacing;

                    if (left + tooltipWidth > window.innerWidth) {
                      left = window.innerWidth - tooltipWidth - 10;
                    }
                    if (left < 10) {
                      left = 10;
                    }

                    const tooltipHeight = 200;
                    if (top + tooltipHeight > window.innerHeight) {
                      top = rect.top - tooltipHeight - spacing;
                    }
                    if (top < 10) {
                      top = 10;
                    }

                    setVisibleTooltip(index);
                    setTooltipPosition({ left, top });
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobileOrTablet) {
                    setVisibleTooltip(null);
                    setTooltipPosition(null);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    if (readOnly) return;
                    const currentAxes = initiative.thematicAxes || [];
                    if (e.target.checked) {
                      onUpdate('thematicAxes', [...currentAxes, option.value]);
                    } else {
                      onUpdate('thematicAxes', currentAxes.filter(axis => axis !== option.value));
                    }
                  }}
                  disabled={readOnly}
                />
                <span className="checkbox-label">{option.label}</span>
                {hasSubejes && isMobileOrTablet && (
                  <span
                    className="modalidad-info-icon"
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      border: '1px solid #d1d5db',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      marginLeft: '8px',
                      display: 'flex'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();

                      if (visibleTooltip === index) {
                        setVisibleTooltip(null);
                        setTooltipPosition(null);
                        return;
                      }

                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 300;
                      const spacing = 10;

                      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                      let top = rect.bottom + spacing;

                      if (left + tooltipWidth > window.innerWidth) {
                        left = window.innerWidth - tooltipWidth - 10;
                      }
                      if (left < 10) {
                        left = 10;
                      }

                      const tooltipHeight = 200;
                      if (top + tooltipHeight > window.innerHeight) {
                        top = rect.top - tooltipHeight - spacing;
                      }
                      if (top < 10) {
                        top = 10;
                      }

                      setVisibleTooltip(index);
                      setTooltipPosition({ left, top });
                    }}
                  >
                    ?
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </FormField>

      {/* Texto "Opcionales" antes del campo de área de la organización */}
      <div className="optional-fields-separator" style={{
        marginTop: '4px',
        marginBottom: initiative.type === 'challenge' ? '0px' : '0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        <div style={{
          flex: 1,
          height: '1px',
          backgroundColor: '#e2e8f0'
        }}></div>
        <span style={{
          fontSize: '12px',
          color: '#94a3b8',
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 400,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap'
        }}>
          Opcionales
        </span>
        <div style={{
          flex: 1,
          height: '1px',
          backgroundColor: '#e2e8f0'
        }}></div>
      </div>

      {/* Área de la organización (si aplica) */}
      <FormField label="Área de la organización">
        <Input
          value={initiative.organizationArea || ''}
          onChange={(e) => onUpdate('organizationArea', e.target.value)}
          onBlur={(e) => handleFieldBlur('organizationArea', e.target.value)}
          placeholder={`Especificar el área de la organización que lleva adelante ${getInitiativePreposition(initiative.type)} ${getInitiativeTypeText(initiative.type)}.`}
          disabled={readOnly}
        />
      </FormField>

      {initiative.type !== 'challenge' && (
        <div className="form-row">
          <FormField label="Estado actual">
            <SearchableSelect
              value={initiative.currentStatus ? (currentStatuses.find(s => s.value === initiative.currentStatus && s.value !== '')?.label || '') : ''}
              onChange={(label) => {
                const status = currentStatuses.find(s => s.label === label && s.value !== '');
                if (status) {
                  onUpdate('currentStatus', status.value);
                } else {
                  // Permitir limpiar el campo
                  onUpdate('currentStatus', '');
                }
              }}
              options={currentStatuses.filter(s => s.value !== '').map(s => s.label)}
              placeholder="Seleccionar estado..."
              allowCustomInput={false}
              disabled={readOnly}
            />
          </FormField>
          {initiative.type === 'solution' && (
            <FormField label="Cómo funciona la solución (Tipo de solución)">
              <SearchableSelect
                value={initiative.solutionType ? (solutionTypes.find(s => s.value === initiative.solutionType && s.value !== '')?.label || '') : ''}
                onChange={(label) => {
                  const type = solutionTypes.find(s => s.label === label && s.value !== '');
                  if (type) {
                    onUpdate('solutionType', type.value);
                  }
                }}
                options={solutionTypes.filter(s => s.value !== '').map(s => s.label)}
                placeholder="Seleccionar tipo..."
                allowCustomInput={false}
                disabled={readOnly}
              />
            </FormField>
          )}
          {initiative.type === 'program' && (
            <FormField label="Número estimado de beneficiarios">
              <SearchableSelect
                value={initiative.estimatedBeneficiaries ? (estimatedBeneficiaries.find(e => e.value === initiative.estimatedBeneficiaries && e.value !== '')?.label || '') : ''}
                onChange={(label) => {
                  const beneficiaries = estimatedBeneficiaries.find(e => e.label === label && e.value !== '');
                  if (beneficiaries) {
                    onUpdate('estimatedBeneficiaries', beneficiaries.value);
                  }
                }}
                options={estimatedBeneficiaries.filter(e => e.value !== '').map(e => e.label)}
                placeholder="Seleccionar rango..."
                allowCustomInput={false}
                disabled={readOnly}
              />
            </FormField>
          )}
          {initiative.type === 'policy' && (
            <FormField label="Tipo de política / instrumento">
              <SearchableSelect
                value={initiative.policyType ? (policyTypes.find(p => p.value === initiative.policyType && p.value !== '')?.label || '') : ''}
                onChange={(label) => {
                  const policy = policyTypes.find(p => p.label === label && p.value !== '');
                  if (policy) {
                    onUpdate('policyType', policy.value);
                  }
                }}
                options={policyTypes.filter(p => p.value !== '').map(p => p.label)}
                placeholder="Seleccionar tipo..."
                allowCustomInput={false}
                disabled={readOnly}
              />
            </FormField>
          )}
        </div>
      )}

      {/* Campos específicos según el tipo */}
      {initiative.type === 'solution' && (
        <div className="form-row">
          <FormField label="Tecnologías utilizadas">
            <MultiSelectTags
              selectedValues={initiative.technologiesUsed || []}
              onChange={(value) => onUpdate('technologiesUsed', value)}
              options={technologiesUsed}
              placeholder="Seleccionar tecnologías..."
              disabled={readOnly}
            />
          </FormField>
          <FormField label="Ámbito / Territorio">
            <Input
              value={initiative.territory || ''}
              onChange={(e) => onUpdate('territory', e.target.value)}
              onBlur={(e) => handleFieldBlur('territory', e.target.value)}
              placeholder="Ej: Nacional, Provincial, Municipal..."
              disabled={readOnly}
            />
          </FormField>
        </div>
      )}

      {/* Ámbito / Territorio para desafíos (sin tecnologías) */}
      {initiative.type === 'challenge' && (
        <FormField label="Ámbito / Territorio">
          <Input
            value={initiative.territory || ''}
            onChange={(e) => onUpdate('territory', e.target.value)}
            onBlur={(e) => handleFieldBlur('territory', e.target.value)}
            placeholder="Ej: Nacional, Provincial, Municipal..."
            disabled={readOnly}
          />
        </FormField>
      )}

      {/* Resto de campos comunes */}
      {/* Ámbito / Territorio solo para programas y políticas (para soluciones y desafíos ya está en form-row) */}
      {initiative.type !== 'solution' && initiative.type !== 'challenge' && (
        <FormField label="Ámbito / Territorio">
          <Input
            value={initiative.territory || ''}
            onChange={(e) => onUpdate('territory', e.target.value)}
            onBlur={(e) => handleFieldBlur('territory', e.target.value)}
            placeholder="Ej: Nacional, Provincial, Municipal..."
            disabled={readOnly}
          />
        </FormField>
      )}

      <FormField label="Población / actores involucrados">
        <Textarea
          value={initiative.population || ''}
          onChange={(e) => onUpdate('population', e.target.value)}
          placeholder={`Describe la población o actores involucrados en ${getInitiativePreposition(initiative.type)} ${getInitiativeTypeText(initiative.type)}...`}
          rows={3}
          maxLength={400}
          showCharCount={true}
          disabled={readOnly}
        />
      </FormField>

      {initiative.type !== 'challenge' && (
        <FormField label="Resultados / evidencias">
          <Textarea
            value={initiative.results || ''}
            onChange={(e) => onUpdate('results', e.target.value)}
            placeholder={`Describe los resultados y evidencias ${getInitiativePreposition(initiative.type)} ${getInitiativeTypeText(initiative.type)}...`}
            rows={4}
            maxLength={400}
            showCharCount={true}
            disabled={readOnly}
          />
        </FormField>
      )}

      <FormField label="Enfoque / metodología">
        <Textarea
          value={initiative.approach || ''}
          onChange={(e) => onUpdate('approach', e.target.value)}
          placeholder={`Describe el enfoque o metodología utilizada en ${getInitiativePreposition(initiative.type)} ${getInitiativeTypeText(initiative.type)}...`}
          rows={3}
          maxLength={400}
          showCharCount={true}
          disabled={readOnly}
        />
      </FormField>

      <FormField label="Alianzas / colaboraciones">
        <Textarea
          value={initiative.alliances || ''}
          onChange={(e) => onUpdate('alliances', e.target.value)}
          placeholder={`Menciona las alianzas o colaboraciones ${getInitiativePreposition(initiative.type)} ${getInitiativeTypeText(initiative.type)}...`}
          rows={3}
          maxLength={400}
          showCharCount={true}
          disabled={readOnly}
        />
      </FormField>

      {/* Documentos de apoyo */}
      <FormField label="Documentos de apoyo">
        <div className="file-upload-container">
          {filePreviews.length > 0 ? (
            <div className="files-list">
              {filePreviews.map((preview, index) => (
                <div key={index} className="file-preview">
                  <p className="file-preview-text">{preview.name}</p>
                  {!readOnly && (
                    <button
                      type="button"
                      className="remove-file-button"
                      onClick={() => handleRemoveFile(index)}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="file-placeholder">
              <span>📄</span>
              <p>Sin archivos</p>
            </div>
          )}
          {!readOnly && (
            <label className="file-upload-btn">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              Agregar Archivos
            </label>
          )}
        </div>
      </FormField>

      {/* Checkbox Solicitar Aprobación */}
        <div style={{ marginTop: '20px' }}>
          <div className="checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={initiative.requestApproval || false}
              onChange={(e) => {
                if (!readOnly) {
                  onUpdate('requestApproval', e.target.checked);
                }
              }}
              disabled={readOnly}
              />
              <span className="checkbox-label">
                {(initiative.type === 'solution' || initiative.type === 'challenge') && 
                  `Solicitar aprobación de la ${getInitiativeTypeText(initiative.type)}`}
                {initiative.type === 'program' && 'Solicitar aprobación del programa de impacto'}
                {initiative.type === 'policy' && 'Solicitar aprobación de la política pública'}
                {!initiative.type && 'Solicitar aprobación de la iniciativa'}
              </span>
            </label>
          </div>
        </div>

      {/* Sección de Contacto */}
      <div className="initiative-section-divider" style={{ marginTop: '40px', paddingTop: '30px', borderTop: '2px solid #e5e7eb' }}>
        <h3 className="initiative-section-title" style={{ 
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '18px',
          fontWeight: 700,
          color: '#1e293b',
          marginBottom: '8px'
        }}>
          Contacto
        </h3>
        <p className="form-section-description" style={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '14px',
          color: '#64748b',
          marginBottom: '20px',
          marginTop: 0
        }}>
          Datos del contacto para interesados en la iniciativa.
        </p>

        {/* Checkbox Iniciativa sin contacto */}
          <div style={{ marginBottom: '20px' }}>
            <div className="checkbox-group">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={initiative.noContact || false}
                  onChange={(e) => {
                  if (readOnly) return;
                    const checked = e.target.checked;

                    // Si se marca, actualizar noContact y limpiar todos los campos de contacto en una sola operación
                    if (checked) {
                      // Limpiar error de email
                      setEmailError('');
                      // Actualizar todos los campos en una sola operación si está disponible
                      if (onUpdateMultiple) {
                        onUpdateMultiple({
                          noContact: true,
                          doNotPublishContact: false,
                          contactFirstName: '',
                          contactLastName: '',
                          contactPosition: '',
                          contactEmail: '',
                          contactPhone: '',
                          contactCodigoPais: '',
                          contactNumeroTelefono: ''
                        });
                      } else {
                        // Fallback: actualizar campo por campo
                        onUpdate('noContact', true);
                        onUpdate('doNotPublishContact', false);
                        onUpdate('contactFirstName', '');
                        onUpdate('contactLastName', '');
                        onUpdate('contactPosition', '');
                        onUpdate('contactEmail', '');
                        onUpdate('contactPhone', '');
                        onUpdate('contactCodigoPais', '');
                        onUpdate('contactNumeroTelefono', '');
                      }
                    } else {
                      // Si se desmarca, solo actualizar noContact
                      onUpdate('noContact', false);
                    }
                  }}
                disabled={readOnly}
                />
                <span className="checkbox-label">Iniciativa sin contacto</span>
              </label>
            </div>
          </div>

        {/* Checkbox No publicar datos de contacto */}
        {!initiative.noContact && (
          <div style={{ marginBottom: '20px' }}>
            <div className="checkbox-group">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={initiative.doNotPublishContact || false}
                  onChange={(e) => {
                    if (!readOnly) {
                      onUpdate('doNotPublishContact', e.target.checked);
                    }
                  }}
                  disabled={readOnly}
                />
                <span className="checkbox-label">No publicar datos de contacto, contactar mediante organizador</span>
              </label>
            </div>
          </div>
        )}

        {/* Campos de contacto */}
        {(!initiative.noContact || readOnly) && (
          <>
            {/* Fila 1: Nombre - Apellido */}
            <div className="form-row">
              <FormField label="Nombre" required={!initiative.noContact}>
                <Input
                  value={initiative.contactFirstName || ''}
                  onChange={(e) => onUpdate('contactFirstName', e.target.value)}
                  onBlur={(e) => handleContactFieldBlur('contactFirstName', e.target.value)}
                  placeholder="Nombre del contacto"
                  disabled={isContactDisabled || readOnly}
                />
              </FormField>

              <FormField label="Apellido" required={!initiative.noContact}>
                <Input
                  value={initiative.contactLastName || ''}
                  onChange={(e) => onUpdate('contactLastName', e.target.value)}
                  onBlur={(e) => handleContactFieldBlur('contactLastName', e.target.value)}
                  placeholder="Apellido del contacto"
                  disabled={isContactDisabled || readOnly}
                />
              </FormField>
            </div>

            {/* Fila 2: Cargo - Correo */}
            <div className="form-row">
              <FormField label="Cargo" required={!initiative.noContact}>
                <Input
                  value={initiative.contactPosition || ''}
                  onChange={(e) => onUpdate('contactPosition', e.target.value)}
                  onBlur={(e) => handleContactFieldBlur('contactPosition', e.target.value)}
                  placeholder="Cargo del contacto"
                  disabled={isContactDisabled || readOnly}
                />
              </FormField>

              <FormField label="Correo" required={!initiative.noContact} error={emailError}>
                <Input
                  type="email"
                  value={initiative.contactEmail || ''}
                  onChange={handleContactEmailChange}
                  onBlur={handleContactEmailBlur}
                  placeholder="correo@ejemplo.com"
                  disabled={isContactDisabled || readOnly}
                  error={!!emailError}
                />
              </FormField>
            </div>

            {/* Fila 3: Número de teléfono */}
            <div className="form-row single-field">
              <FormField label="Número de teléfono">
                <PhoneInput
                  codigoPais={initiative.contactCodigoPais || 'AR'}
                  numeroTelefono={initiative.contactNumeroTelefono || ''}
                  onCodigoPaisChange={handleContactCodigoPaisChange}
                  onNumeroTelefonoChange={handleContactNumeroTelefonoChange}
                  disabled={isContactDisabled || readOnly}
                />
              </FormField>
            </div>
          </>
        )}
      </div>

      {/* Sección de Rondas de Negocios */}
      <div className="initiative-section-divider" style={{ marginTop: '40px', paddingTop: '30px', borderTop: '2px solid #e5e7eb' }}>
        <h3 className="initiative-section-title" style={{ 
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '18px',
          fontWeight: 700,
          color: '#1e293b',
          marginBottom: '20px'
        }}>
          Rondas de Negocios
        </h3>

          <div className="checkbox-group" style={{ marginBottom: '12px' }}>
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={initiative.interestedInBusinessRounds || false}
              onChange={(e) => {
                if (!readOnly) {
                  onUpdate('interestedInBusinessRounds', e.target.checked);
                }
              }}
              disabled={readOnly}
              />
              <span className="checkbox-label">
                Estoy interesado en coordinar encuentros de negocios en Smart City Expo Santiago del Estero 2026 (20 - 21 de mayo)
              </span>
            </label>
          </div>

          <div className="checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={initiative.interestedInBusinessRoundsOutsideEvent || false}
              onChange={(e) => {
                if (!readOnly) {
                  onUpdate('interestedInBusinessRoundsOutsideEvent', e.target.checked);
                }
              }}
              disabled={readOnly}
              />
              <span className="checkbox-label">
                Estoy interesado en coordinar encuentros de negocios por fuera del evento
              </span>
            </label>
          </div>
      </div>
        </>
        )}
        
        {/* Badges de estado al final (solo en readonly del popup) */}
        {readOnly && showApprovalBadge && (() => {
          const statusInfo = getInitiativeStatusHelper(initiative);
          // Solo mostrar selector de aprobación si está completo o tiene solicitud de aprobación
          const shouldShowApprovalSelector = statusInfo.completionStatus === 'complete' || statusInfo.hasRequestApproval;
          
          return (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                {/* Badge de estado de completitud - siempre se muestra si corresponde */}
                <span className={`participation-status-badge status-${statusInfo.completionStatus}`}>
                  {statusInfo.completionStatus === 'complete' && <><CheckCircleIcon width={12} height={12} /> Completo</>}
                  {statusInfo.completionStatus === 'incomplete' && <><XCircleIcon width={12} height={12} /> Incompleto</>}
              </span>
                {/* Badge de solicitado - siempre se muestra si corresponde */}
                {statusInfo.hasRequestApproval && (
                  <span className="participation-status-badge status-requested">
                    <AlertTriangleIcon width={12} height={12} /> Solicitado
                  </span>
                )}
                {/* Selector de estado de aprobación - solo se muestra si está completo o tiene solicitud */}
                {shouldShowApprovalSelector && onApprovalStatusChange && (
                  <div style={{ minWidth: '120px', maxWidth: '140px', flexShrink: 0 }}>
                    <CustomSelect
                      className="admin-filter-select admin-filter-select-small"
                      value={initiative.approval_status || 'pending'}
                      onChange={(newStatus) => {
                        if (onApprovalStatusChange) {
                          onApprovalStatusChange(initiative.id, newStatus as 'pending' | 'approved' | 'rejected');
                        }
                      }}
                      options={[
                        { value: 'pending', label: 'Pendiente', color: '#3b82f6' },
                        { value: 'approved', label: 'Aprobado', color: '#10b981' },
                        { value: 'rejected', label: 'Rechazado', color: '#ef4444' }
                      ]}
                    />
      </div>
        )}
              </div>
            </div>
          );
        })()}
      </div>
      )}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={() => {
          onRemove();
          setShowDeleteModal(false);
        }}
        confirmText="Aceptar"
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de que deseas eliminar esta iniciativa?</p>
      </Modal>
    </div>
    );
  };

export default InitiativesSection;
